import { useCallback, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import type { LeDataChatMessage } from '~/types/leData';

import {
  BLOCK_ID_FILENAME_LENGTH,
  SESSION_ID_FILENAME_LENGTH,
  buildExportFilename,
} from '~/utils/leData/conversationPdfExporter';

const CHART_NODE_SELECTOR_ATTR = 'data-le-data-chart-id';

function getChartNode(blockId: string): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }
  return document.querySelector<HTMLElement>(`[${CHART_NODE_SELECTOR_ATTR}="${blockId}"]`);
}

function getMessageIdShort(messages: ReadonlyArray<LeDataChatMessage>): string {
  /** For conversation-scope, anchor on the most recent message id so multiple exports of the same chat differ deterministically. */
  const last = messages[messages.length - 1];
  if (last) {
    return last.id.slice(0, BLOCK_ID_FILENAME_LENGTH);
  }
  return 'empty';
}

export interface UseExportPdfResult {
  exporting: boolean;
  exportConversation: (messages: ReadonlyArray<LeDataChatMessage>, sessionId: string | null) => Promise<void>;
  exportMessage: (message: LeDataChatMessage, sessionId: string | null) => Promise<void>;
}

export function useExportPdf(): UseExportPdfResult {
  const { enqueueSnackbar } = useSnackbar();
  const [exporting, setExporting] = useState(false);
  /** Synchronous guard so rapid double-clicks within the same render tick
   *  cannot start two concurrent export pipelines. The state-based `exporting`
   *  alone is bypassed if both clicks occur before React schedules the next render. */
  const exportingRef = useRef(false);

  const exportConversation = useCallback(
    async (messages: ReadonlyArray<LeDataChatMessage>, sessionId: string | null) => {
      if (exportingRef.current || messages.length === 0) {
        return;
      }
      exportingRef.current = true;
      setExporting(true);
      try {
        const { exportConversationToPdf } = await import('~/utils/leData/conversationPdfExporter');
        const sessionIdShort = sessionId ? sessionId.slice(0, SESSION_ID_FILENAME_LENGTH) : null;
        const filename = buildExportFilename('conversation', sessionIdShort, getMessageIdShort(messages));
        await exportConversationToPdf(messages, { filename, getChartNode });
      } catch (err) {
        console.error('PDF export failed:', err);
        enqueueSnackbar('Failed to export PDF', { variant: 'error' });
      } finally {
        exportingRef.current = false;
        setExporting(false);
      }
    },
    [enqueueSnackbar],
  );

  const exportMessage = useCallback(
    async (message: LeDataChatMessage, sessionId: string | null) => {
      if (exportingRef.current) {
        return;
      }
      exportingRef.current = true;
      setExporting(true);
      try {
        const { exportMessageToPdf } = await import('~/utils/leData/conversationPdfExporter');
        const sessionIdShort = sessionId ? sessionId.slice(0, SESSION_ID_FILENAME_LENGTH) : null;
        const idShort = message.id.slice(0, BLOCK_ID_FILENAME_LENGTH);
        const filename = buildExportFilename('message', sessionIdShort, idShort);
        await exportMessageToPdf(message, { filename, getChartNode });
      } catch (err) {
        console.error('PDF export failed:', err);
        enqueueSnackbar('Failed to export PDF', { variant: 'error' });
      } finally {
        exportingRef.current = false;
        setExporting(false);
      }
    },
    [enqueueSnackbar],
  );

  return { exporting, exportConversation, exportMessage };
}
